import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Table,
} from "antd";
import FormItem from "antd/lib/form/FormItem";
import { useNavigate, useParams } from "react-router-dom";
import * as _ from 'lodash';

import { NewModal } from "../NewModal";
import { useTemplateOperationTypes } from "../../hooks/useTemplateOperationTypes";

export interface ITemplateOperationType {
  id: string;
  title: string;
  description: string;
  user_id?: string;
  company_id?: string;
}

interface IEditTemplateType extends ITemplateOperationType {
  onFinish?: any;
  onCancel?: any;
}

const FormEdit = ({ id, title, description, onFinish, onCancel, }: IEditTemplateType) => {

  const handleFinish = (form: any) => {
    onFinish({...form});
  }

  return (
    <Form
      onFinish={handleFinish}
      initialValues={{ title, description }}
    >
      <Form.Item
        name="title"
        label="Titulo"
        style={{
          marginBottom: "25px",
        }}
        rules={[
          {
            required: true,
            message: "Este campo é obrigatório!",
          },
        ]}
      >
        <Input
          maxLength={50}
          placeholder="Titulo do Projeto!"
        />
      </Form.Item>

      <Form.Item
        name="description"
        label="Descrição"
        style={{
          marginBottom: "25px",
        }}
        rules={[
          {
            required: true,
            message: "Este campo é obrigatório!",
          },
        ]}
      >
        <Input maxLength={50} placeholder="Descrição" />
      </Form.Item>

      <FormItem style={{ paddingLeft: "63%" }}>
        <Button type="primary" htmlType="submit">
          Salvar
        </Button>
        <Button onClick={onCancel}>Cancelar</Button>
      </FormItem>
    </Form>
  )
};

export function TemplateOperationTypesTable() {
  const {
    fetchTemplateType,
    updateTemplateType,
    deleteTemplateType,
    loading,
    templateTypes,
    total,
  } = useTemplateOperationTypes();

  const currentRoute = useParams();
  const navigate = useNavigate();
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [page, setPage] = useState<number>(Number(currentRoute.page));
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState<string>("title");
  const [formDefault, setFormDefault] = useState<ITemplateOperationType>({} as ITemplateOperationType);
  const [search, setSearch] = useState<string>("");

  const fetchProjectDebounce = _.debounce(() => {
    return fetchTemplateType(page, order, orderBy, search);
  }, 500);

  useEffect(() => {
    if (page && order && orderBy) {
      fetchProjectDebounce();
    }
  }, [page, order, orderBy]);

  const handleCancelEdit = () => {
    setVisibleEdit(false);
  };

  const handleSaveEdit = async (id: string, form: any) => {
    setVisibleEdit(false);
    const status: any = await updateTemplateType(id, form);

    if (status?.status === 406) {
      return message.warning("Existem usuários nesse grupo!");
    }

    if (status?.status !== 200) {
      return message.error("Falha!");
    }

    fetchTemplateType(page, order, orderBy);
    message.success("Editado!");
  }

  const orderbyCresDesc = (pagination: any, filters: any, sorter: any, extra: any) => {
    if (sorter.field) {
      setOrderBy(sorter.field);
    }

    if (sorter.order) {
      setOrder(sorter.order === "descend" ? "desc" : "asc");
    }
  }

  const confirmExclusion = async (record: any) => {
    const status: any = await deleteTemplateType(record.id);
    if(status?.response?.status === 500 || status?.response?.status === 406){
      message.warning('Falha')
    } else {
      message.success("Excluido")
    }
  }

  const columns: any = [
    {
      title: "Nome",
      dataIndex: "title",
      key: "title",
      width: "40%",
      sorter: (a: ITemplateOperationType, b: ITemplateOperationType) => a.title.localeCompare(b.title),
    },

    {
      title: "Descrição",
      dataIndex: "description",
      key: "description",
      filterSearch: true,
      width: "40%",
      sorter: (a: ITemplateOperationType, b: ITemplateOperationType) => a.description.localeCompare(b.description),
      onFilter: (value: any, record: any) => record.role.startsWith(value),
    },
    {
      title: "Ações",
      key: "action",
      render: (text: string, record: ITemplateOperationType) => (
        <Space size="middle">
          <a
            style={{
              color: "#F7CD11",
            }}
            onClick={() => {
              setFormDefault(record as IEditTemplateType);
              setVisibleEdit(true);
            }}
          >
            Editar
          </a>

          <Popconfirm
            title="Você tem certeza?"
            onConfirm={(e) => {
              e?.stopPropagation();
              confirmExclusion(record);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Sim"
            cancelText="Não"
          >
            <a
              style={{
                color: "#ff0000",
              }}
              onClick={(e) => e.stopPropagation()}
            >

              Deletar
            </a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        showSorterTooltip={{ title: "Clique para ordenar" }}
        dataSource={templateTypes}
        columns={columns}
        rowKey={(item) => item.id as string}
        onChange={orderbyCresDesc}
        loading={loading}
        pagination={{
          position: ["bottomRight"],
          pageSize: 7,
          current: Number(page),
          total: total,
          onChange: (pageAux) => {
            navigate(`/template-operation-types/${pageAux}`);
            setPage(pageAux);
          },
        }}
      />

      {
        visibleEdit && (
          <NewModal
            buttonModalText={"Editar"}
            visible={visibleEdit}
            handleCancel={handleCancelEdit}
            handleOk={() => null}
            title="Editar tipo"
          >
            <FormEdit
              id={formDefault.id}
              title={formDefault.title}
              description={formDefault.description}
              onFinish={(form: any) => handleSaveEdit(formDefault.id, form)}
              onCancel={() => setVisibleEdit(false)}
            />
          </NewModal>
        )
      }
    </>
  );
}
